import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import { ReportActions } from '../../../components/ui/ReportActions';
import { Multiselect } from '../../../components/ui/Multiselect/Multiselect';
import useInvoiceReport from './useInvoiceReport';
import Container from '../../../components/ui/Container/Container';
import { Row, Col, Form, Button, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Helpers } from '../../../utils/helpers';
import { IKeyValuePair } from '../../../utils/constants/interfaces';
import styles from './InvoiceReport.module.css';

interface InvoiceReportProps {
  visible?: boolean;
}

export const InvoiceReport = ({ visible = true }: InvoiceReportProps) => {
  const manager = useInvoiceReport();

  const handleClearFilters = () => {
    if (manager.resetSearch) {
      manager.resetSearch();
    }
  };

  const handleSearch = () => {
    manager.triggerSearch();
  };

  // Convert member status options to multiselect format
  const memberStatusMultiselectOptions: IKeyValuePair<string>[] = manager.memberStatusOptions.map(
    (option) => ({
      key: option.id.toString(),
      value: option.description
    })
  );

  // Convert membership type options to multiselect format
  const membershipTypeMultiselectOptions: IKeyValuePair<string>[] =
    manager.membershipTypeOptions.map((option) => ({
      key: option.id.toString(),
      value: option.name
    }));

  const handleMemberStatusChange = (selectedValues: string[]) => {
    const memberStatusIds = selectedValues
      .map((value) => parseInt(value))
      .filter((id) => !isNaN(id));
    manager.updateFilter('memberStatusIds', memberStatusIds);
  };

  const handleMembershipTypeChange = (selectedValues: string[]) => {
    const membershipTypeIds = selectedValues
      .map((value) => parseInt(value))
      .filter((id) => !isNaN(id));
    manager.updateFilter('membershipTypeIds', membershipTypeIds);
  };

  // Get selected keys (IDs as strings) for the multiselects
  const getSelectedMemberStatusDisplayTexts = (): string[] => {
    return manager.searchParameters.memberStatusIds.map((id) => id.toString());
  };

  const getSelectedMembershipTypeDisplayTexts = (): string[] => {
    return manager.searchParameters.membershipTypeIds.map((id) => id.toString());
  };

  if (!visible) return <></>;

  return (
    <Container visible={true} className={styles.reportContainer}>
      {/* Make the form wider to accommodate more fields per row */}
      <Row className="justify-content-center">
        <Col xl={10} lg={11} md={12}>
          <div className={styles.filterSection}>
            {/* Filter Header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Invoice Report</h5>
            </div>

            {/* Row 1: Billing Year, Balance filters */}
            <Row className="g-2 mb-2">
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Billing Year</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Billing Year"
                    value={manager.searchParameters.billingYear?.toString() || ''}
                    onChange={(e) => manager.updateFilter('billingYear', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Balance &gt;=</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Min Balance"
                    value={manager.searchParameters.balanceGreaterThanOrEqual || ''}
                    onChange={(e) =>
                      manager.updateFilter('balanceGreaterThanOrEqual', e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Balance &lt;=</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Max Balance"
                    value={manager.searchParameters.balanceLessThanOrEqual || ''}
                    onChange={(e) => manager.updateFilter('balanceLessThanOrEqual', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Member Status</Form.Label>
                  <Multiselect
                    options={memberStatusMultiselectOptions}
                    selectedOptions={getSelectedMemberStatusDisplayTexts()}
                    onChange={handleMemberStatusChange}
                    size="small"
                    maxSelectedItemWidth={200}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Member Type</Form.Label>
                  <Multiselect
                    options={membershipTypeMultiselectOptions}
                    selectedOptions={getSelectedMembershipTypeDisplayTexts()}
                    onChange={handleMembershipTypeChange}
                    size="small"
                    maxSelectedItemWidth={200}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Action Buttons */}
            <Row className="g-2">
              <Col className="d-flex justify-content-start align-items-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSearch}
                  disabled={manager.loading}
                  className="me-2">
                  <FontAwesomeIcon icon={faSearch} className="me-1" />
                  Search
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  disabled={manager.loading}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear
                </Button>
              </Col>
            </Row>
          </div>

          {/* Results Section */}
          <div className={styles.resultsSection}>
            <LoadingContainer isLoading={manager.loading}>
              {!manager.hasPerformedSearch && (
                <div className="text-center py-4 text-muted">
                  <p>
                    Click &ldquo;Search&rdquo; to view invoices. You can optionally add filters
                    above to narrow your results.
                  </p>
                </div>
              )}
              {manager.hasPerformedSearch && manager.invoicePagedResults.resultCount === 0 && (
                <div className="text-center py-4 text-muted">
                  No invoices found matching your criteria.
                </div>
              )}
              {manager.invoicePagedResults.resultCount > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>{manager.invoicePagedResults.resultCount}</strong> invoices found
                    </div>
                    <ReportActions
                      data={manager.getSortedResultsForExport()}
                      columns={manager.getReportColumns()}
                      reportTitle="Invoice Report"
                      csvFileName="invoiceReportExport.csv"
                      searchCriteria={manager.getSearchCriteria()}
                      size="sm"
                      variant="outline-primary"
                    />
                  </div>

                  <Table striped hover responsive className={styles.resultsTable}>
                    <thead>
                      <tr>
                        <SortableHeader
                          text="Club Member ID"
                          sortField="clubMemberId"
                          active={manager.searchParameters.sortField === 'clubMemberId'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="First Name"
                          sortField="firstName"
                          active={manager.searchParameters.sortField === 'firstName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Last Name"
                          sortField="lastName"
                          active={manager.searchParameters.sortField === 'lastName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Membership Type"
                          sortField="memberTypeName"
                          active={manager.searchParameters.sortField === 'memberTypeName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Full Rate"
                          sortField="memberTypeAmount"
                          active={manager.searchParameters.sortField === 'memberTypeAmount'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Discount"
                          sortField="discountApplied"
                          active={manager.searchParameters.sortField === 'discountApplied'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Initiation Fee"
                          sortField="initiationFee"
                          active={manager.searchParameters.sortField === 'initiationFee'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Late Fee"
                          sortField="lateFee"
                          active={manager.searchParameters.sortField === 'lateFee'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="New This Year"
                          sortField="newThisYear"
                          active={manager.searchParameters.sortField === 'newThisYear'}
                          onClick={manager.changeSort}
                        />
                        <th>Discount Basis</th>
                        <SortableHeader
                          text="Work Hours"
                          sortField="workHours"
                          active={manager.searchParameters.sortField === 'workHours'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Dues"
                          sortField="dues"
                          active={manager.searchParameters.sortField === 'dues'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Payments"
                          sortField="amountPaidForBillingYear"
                          active={manager.searchParameters.sortField === 'amountPaidForBillingYear'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Balance"
                          sortField="balanceDue"
                          active={manager.searchParameters.sortField === 'balanceDue'}
                          onClick={manager.changeSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {manager.invoicePagedResults.results?.map((invoice) => (
                        <tr
                          key={`${invoice.clubMemberId}-${invoice.billingYear}`}
                          className={styles.clickableRow}
                          onClick={() => window.open(`/members/${invoice.clubMemberId}`, '_blank')}>
                          <td>{invoice.clubMemberId}</td>
                          <td>{invoice.firstName}</td>
                          <td>{invoice.lastName}</td>
                          <td>{invoice.memberTypeName}</td>
                          <td>{Helpers.formatCurrency(invoice.memberTypeAmount)}</td>
                          <td>{invoice.discountApplied.toFixed(2)}%</td>
                          <td>{Helpers.formatCurrency(invoice.initiationFee)}</td>
                          <td>{Helpers.formatCurrency(invoice.lateFee)}</td>
                          <td>{invoice.newThisYear ? 'Yes' : 'No'}</td>
                          <td>{invoice.discountReason || '-'}</td>
                          <td>{invoice.workHours}</td>
                          <td>{Helpers.formatCurrency(invoice.dues)}</td>
                          <td>{Helpers.formatCurrency(invoice.amountPaidForBillingYear)}</td>
                          <td>{Helpers.formatCurrency(invoice.balanceDue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="pt-4">
                    <PagingControls
                      page={manager.invoicePagedResults.currentPage ?? 1}
                      pageCount={manager.invoicePagedResults.pageCount ?? 1}
                      maxPlusOrMinusPagesToShow={3}
                      onPageChange={manager.changePage}
                    />
                  </div>
                </>
              )}
            </LoadingContainer>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default InvoiceReport;
